import {
  createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState,
  type ReactNode,
} from 'react';
import type { AnalyticsEvent } from '../core/events';
import { thumbnailKindOf } from '../core/thumbnails';
import {
  uploadWithProgress,
  type GetToken, type MaterialSummary, type UploadOutcome, type UploadProgressOptions,
} from './api';
import { pdfJsEngine, type PdfEngine } from './pdf';
import { makeThumbnail, sourceFromFile, thumbnailCtx } from './thumbnails';
import UploadPanel from './components/UploadPanel';
import {
  EMPTY_QUEUE, isActive, nextStep, reduceQueue, titleFor, type UploadItem,
} from './upload-queue';

/**
 * The imperative shell around `upload-queue.ts`: it holds the files, runs one upload at a
 * time, and turns what the request reports into the reducer's events.
 *
 * Everything that touches the world comes in through `UploadCtx`, so a component test hands
 * in a fake and drives each upload by hand. The default is `uploadWithProgress`.
 *
 * It is mounted above the routes (`App.tsx`), not inside the room, so a file keeps going
 * while the teacher opens the material that finished before it and comes back.
 */

export interface UploadCtx {
  upload: (
    groupId: string,
    file: File,
    title: string,
    note: string,
    options: UploadProgressOptions,
  ) => Promise<UploadOutcome>;
  /**
   * The thumbnail step. Absent, a file goes straight from its 2xx to `done`. Whatever it does, a
   * failure here never makes the row an error.
   */
  makeThumbnail?: (material: MaterialSummary, file: File) => Promise<unknown>;
  /**
   * Whether this material gets the `thumbnail` step at all. Absent, every one does. A Word
   * file has no thumbnail to make, so it goes straight to `done` rather than flashing
   * "Making a preview…".
   */
  wantsThumbnail?: (material: MaterialSummary) => boolean;
  now?: () => number;
}

/** When a 429 carries no `Retry-After`: the limiter's whole window. */
const DEFAULT_RETRY_SECONDS = 60;

export interface UploadQueueApi {
  items: UploadItem[];
  add: (groupId: string, files: File[]) => void;
  edit: (id: string, change: { title?: string; note?: string }) => void;
  cancel: (id: string) => void;
  dismiss: () => void;
}

export function useUploadQueue(ctx: UploadCtx): UploadQueueApi {
  const [state, dispatch] = useReducer(reduceQueue, EMPTY_QUEUE);
  const [tick, setTick] = useState(0);
  const files = useRef(new Map<string, File>());
  const controllers = useRef(new Map<string, AbortController>());
  const running = useRef<string | null>(null);
  const minted = useRef(0);
  const context = useRef(ctx);
  useEffect(() => { context.current = ctx; }, [ctx]);

  useEffect(() => {
    if (running.current !== null) return undefined;
    const now = context.current.now ?? Date.now;
    const step = nextStep(state, now());
    if (step.kind === 'wait') {
      const timer = setTimeout(() => setTick((n) => n + 1), step.ms);
      return () => clearTimeout(timer);
    }
    if (step.kind !== 'start') return undefined;

    const { item } = step;
    const file = files.current.get(item.id);
    if (!file) return undefined;

    const { id } = item;
    const controller = new AbortController();
    running.current = id;
    controllers.current.set(id, controller);
    dispatch({ type: 'start', id });

    const ctxNow = context.current;
    void (async () => {
      const outcome: UploadOutcome = await ctxNow.upload(item.groupId, file, titleFor(item), item.note, {
        onProgress: (sent, total) => dispatch({ type: 'progress', id, sent, total }),
        onSent: () => dispatch({ type: 'sent', id }),
        signal: controller.signal,
      }).catch((): UploadOutcome => ({ ok: false, reason: 'network', status: 0 }));

      controllers.current.delete(id);
      running.current = null;

      if (outcome.ok) {
        const wanted = ctxNow.makeThumbnail !== undefined
          && (ctxNow.wantsThumbnail?.(outcome.material) ?? true);
        const make = wanted ? ctxNow.makeThumbnail : undefined;
        dispatch({ type: 'ok', id, material: outcome.material, thumbnail: make !== undefined });
        if (make) {
          void Promise.resolve()
            .then(() => make(outcome.material, file))
            .then(
              () => dispatch({ type: 'thumbnail_done', id }),
              () => dispatch({ type: 'thumbnail_failed', id }),
            )
            .finally(() => files.current.delete(id));
        } else {
          files.current.delete(id);
        }
      } else if (outcome.reason === 'cancelled') {
        // The cancel is already in the queue; the abort was its echo.
        files.current.delete(id);
      } else if (outcome.status === 429) {
        const wait = (outcome.retryAfter ?? DEFAULT_RETRY_SECONDS) * 1000;
        dispatch({ type: 'rate_limited', id, retryAt: now() + wait });
      } else if (outcome.reason === 'network') {
        dispatch({ type: 'network_error', id });
      } else {
        dispatch({ type: 'refused', id, reason: outcome.reason });
      }
      // `running` is a ref, so clearing it re-renders nothing. This does.
      setTick((n) => n + 1);
    })();
    return undefined;
  }, [state, tick]);

  // Leaving the app abandons whatever is still going, the way closing the tab would.
  useEffect(() => {
    const live = controllers.current;
    return () => { for (const controller of live.values()) controller.abort(); };
  }, []);

  // A tab closed mid-upload loses it. Say so, the one time it matters.
  const unsent = state.items.some((item) => item.state === 'queued' || item.state === 'uploading');
  useEffect(() => {
    if (!unsent) return undefined;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsent]);

  const add = useCallback((groupId: string, chosen: File[]) => {
    if (chosen.length === 0) return;
    const metas = chosen.map((file) => {
      minted.current += 1;
      const id = `upload-${minted.current}`;
      files.current.set(id, file);
      return { id, name: file.name, size: file.size, type: file.type };
    });
    dispatch({ type: 'add', groupId, files: metas });
  }, []);

  const edit = useCallback((id: string, change: { title?: string; note?: string }) => {
    dispatch({ type: 'edit', id, ...change });
  }, []);

  const cancel = useCallback((id: string) => {
    dispatch({ type: 'cancel', id });
    controllers.current.get(id)?.abort();
  }, []);

  const dismiss = useCallback(() => {
    for (const item of state.items) if (!isActive(item)) files.current.delete(item.id);
    dispatch({ type: 'dismiss' });
  }, [state.items]);

  return useMemo(
    () => ({ items: state.items, add, edit, cancel, dismiss }),
    [state.items, add, edit, cancel, dismiss],
  );
}

const UploadsContext = createContext<UploadQueueApi | null>(null);

/** The queue, where one is mounted. `null` in a component rendered without the provider. */
export const useUploads = (): UploadQueueApi | null => useContext(UploadsContext);

export interface UploadsProviderProps {
  getToken: GetToken;
  track: (event: AnalyticsEvent) => void;
  /**
   * Injected in tests. The default uploads over XHR with the bearer token, then draws the
   * material's thumbnail in this tab and stores it.
   */
  ctx?: UploadCtx;
  /** The PDF engine the default thumbnail step draws with. Injected in tests. */
  pdf?: PdfEngine;
  children: ReactNode;
}

export function UploadsProvider({ getToken, track, ctx, pdf = pdfJsEngine, children }: UploadsProviderProps) {
  const fallback = useMemo<UploadCtx>(() => ({
    upload: (groupId, file, title, note, options) =>
      uploadWithProgress(getToken, groupId, { file, title, note }, track, options),
    wantsThumbnail: (material) => thumbnailKindOf(material.contentType) !== null,
    makeThumbnail: async (material, file) => {
      const source = await sourceFromFile(material.contentType, file);
      if (!source) return null;
      return makeThumbnail(thumbnailCtx(getToken, pdf), material.id, source);
    },
  }), [getToken, track, pdf]);
  const queue = useUploadQueue(ctx ?? fallback);
  return (
    <UploadsContext.Provider value={queue}>
      {children}
      <UploadPanel
        items={queue.items}
        onCancel={queue.cancel}
        onEdit={(id, title) => queue.edit(id, { title })}
        onDismiss={queue.dismiss}
      />
    </UploadsContext.Provider>
  );
}
